/*
 * Utility helpers to intercept the browser back button (desktop hardware keys
 * and mobile gestures alike) so that modals can close gracefully instead of
 * navigating away from the current page.
 */

import type { DependencyList } from "react";
import { useEffect } from "react";

export interface BackButtonInterceptorOptions {
	/**
	 * Called when the user presses the browser/hardware back button. Close your
	 * modal inside this callback.
	 */
	onBack: () => void;
	/**
	 * Provide a stable identifier when stacking multiple interceptors. Mostly
	 * useful for debugging; defaults to a generated value.
	 */
	stateId?: string;
	/**
	 * Set to false if you do not want this interceptor to push a new entry into
	 * the history stack (advanced use only).
	 */
	pushHistoryEntry?: boolean;
}

export type BackButtonCleanup = () => void;

interface HandlerEntry {
	id: string;
	onBack: () => void;
	pushHistoryEntry: boolean;
	markRemovedByPop: () => void;
}

const handlerStack: HandlerEntry[] = [];
let isListenerAttached = false;
let ignoreNextPopState = false;

const generateId = () =>
	`modal-back-${Date.now().toString(36)}-${Math.random()
		.toString(36)
		.slice(2, 8)}`;

const ensureListener = () => {
	if (typeof window === "undefined") {
		return;
	}
	if (!isListenerAttached) {
		window.addEventListener("popstate", handlePopState);
		isListenerAttached = true;
	}
};

const detachListenerIfNeeded = () => {
	if (typeof window === "undefined") {
		return;
	}
	if (isListenerAttached && handlerStack.length === 0) {
		window.removeEventListener("popstate", handlePopState);
		isListenerAttached = false;
	}
};

const handlePopState = (event: PopStateEvent) => {
	if (ignoreNextPopState) {
		ignoreNextPopState = false;
		return;
	}

	const entry = handlerStack.pop();
	if (!entry) {
		detachListenerIfNeeded();
		return;
	}

	entry.markRemovedByPop();

	try {
		entry.onBack();
	} catch (error) {
		console.error("Error while handling back button interceptor", error);
	}

	detachListenerIfNeeded();
};

/**
 * Registers a back-button interceptor that closes the active modal instead of
 * navigating back. Returns a cleanup function that must be called when the
 * modal closes programmatically.
 */
export const interceptBackButton = (
	options: BackButtonInterceptorOptions
): BackButtonCleanup => {
	if (typeof window === "undefined" || typeof window.history === "undefined") {
		return () => undefined;
	}

	const id = options.stateId ?? generateId();
	const pushHistoryEntry = options.pushHistoryEntry !== false;
	let removed = false;
	let removedByPop = false;

	const entry: HandlerEntry = {
		id,
		onBack: options.onBack,
		pushHistoryEntry,
		markRemovedByPop: () => {
			removedByPop = true;
			removed = true;
		},
	};

	handlerStack.push(entry);
	ensureListener();

	if (pushHistoryEntry) {
		const marker = { __modalBackInterceptor: true, id };
		try {
			window.history.pushState(marker, "", window.location.href);
		} catch (error) {
			console.warn("Failed to push history state for modal interceptor", error);
		}
	}

	const cleanup: BackButtonCleanup = () => {
		if (removed) {
			return;
		}
		removed = true;

		const index = handlerStack.findIndex((item) => item.id === id);
		if (index !== -1) {
			handlerStack.splice(index, 1);
		}

		detachListenerIfNeeded();

		if (pushHistoryEntry && !removedByPop) {
			// Only go back if we are currently in the state we pushed
			const currentState = window.history.state;
			if (
				currentState &&
				currentState.__modalBackInterceptor &&
				currentState.id === id
			) {
				ignoreNextPopState = true;
				try {
					window.history.back();
				} catch (error) {
					console.warn(
						"Failed to step back in history when cleaning interceptor",
						error
					);
					ignoreNextPopState = false;
				}
			}
		}
	};

	return cleanup;
};

/**
 * React hook wrapper around {@link interceptBackButton}. Pass `isActive = true`
 * whenever the modal becomes visible; the hook will automatically register and
 * clean up the interceptor.
 */
import { useLayoutEffect } from 'react';

// Use useLayoutEffect to ensure state is pushed before any layout changes (like body scroll locking)
export const useBackButtonInterceptor = (
	isActive: boolean,
	onBack: () => void,
	deps: DependencyList = []
) => {
	useLayoutEffect(() => {
		if (!isActive) {
			return;
		}

		const cleanup = interceptBackButton({ onBack });
		return () => {
			cleanup();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isActive, ...deps]);
};
